import { useCallback, useMemo } from 'react';
import * as Yup from 'yup';
import { Form, Formik, FormikConsumer, FormikHelpers } from 'formik';
import { Trans, useTranslation } from 'react-i18next';
import {
  Criterion,
  Session,
  StudyType,
  useIsAdmin,
  useMarkStudyStepAsValidated,
  usePlan,
  usePriceTiers,
  useRecruitment,
  useTaskSetting,
  useUpdateCriteria,
  useUpdateModeratedTaskSetting,
  useUpdateRecruitment,
  useUpdateStudy,
} from '@app/api';
import {
  Button,
  CenteredLoader,
  CenteredModal,
  useBoolModalState,
  useToast,
} from 'components';
import { hasProduct } from 'lib/billing';

import { EditSessionLayout } from './components/EditSessionLayout';
import { SessionSubmitLine } from './components/SessionSubmitLine';
import { useRedirectIfDraft } from './hooks/useRedirectIfDraft';
import {
  getSessionInitialValue,
  getSessionValidationSchema,
  StudySettingFields,
} from './components/StudySettingFields';
import {
  getModeratedTaskSettingInitialValue,
  getModeratedTaskSettingValidationSchema,
  ModeratedTaskSettingFields,
} from './components/ModeratedTaskSettingFields';
import { NDADropzone } from './components/NDADropzone';
import { updateQuotasValues } from '/lib/criterionQuotaUtils';

interface Props {
  session: Session;
}

export const EditSessionSettings = ({ session }: Props) => {
  const { t } = useTranslation();
  const isAdmin = useIsAdmin();
  const toast = useToast();

  const [
    confirmationModalOpened,
    openConfirmationModal,
    baseCloseConfirmationModal,
  ] = useBoolModalState(false);
  const { data: plan } = usePlan(session.planId);
  const { data: taskSetting } = useTaskSetting(session.taskSettingId);
  const { data: recruitment } = useRecruitment(session.recruitmentId);
  const { data: priceThiers } = usePriceTiers({
    enabled: !!plan?.priceThierId,
  });
  const priceTier = useMemo(
    () => priceThiers?.find(({ id }) => id === plan?.priceThierId),
    [priceThiers, plan],
  );

  const { mutateAsync: updateStudy } = useUpdateStudy(session.id);
  const { mutateAsync: updateRecruitment } = useUpdateRecruitment(
    session.recruitmentId,
  );
  const { mutateAsync: updateModeratedTaskSetting } =
    useUpdateModeratedTaskSetting(session.taskSettingId);
  const { mutateAsync: updateCriteria } = useUpdateCriteria(
    session.recruitmentId,
  );

  const { mutateAsync: markStepAsValidated } = useMarkStudyStepAsValidated(
    session.id,
  );

  const participantLimits = useMemo(() => {
    const limit = {
      min: session.numberOfBooked || 1,
      max: Number.MAX_SAFE_INTEGER,
    };
    if (isAdmin) return limit;
    if (priceTier) {
      if (priceTier.maximum) {
        limit.max = priceTier.maximum;
      }
      if (priceTier.minimum > limit.min) {
        limit.min = priceTier.minimum;
      }
    }

    if (plan && hasProduct(plan.products, 'LIMIT_PARTICIPANTS_TO_10')) {
      limit.max = 10;
    } else if (plan && hasProduct(plan.products, 'LIMIT_PARTICIPANTS_TO_300')) {
      limit.max = 300;
    }

    return limit;
  }, [isAdmin, plan, priceTier, session.numberOfBooked]);

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        session: getSessionValidationSchema(
          participantLimits,
          session.studyType,
        ),
        recruitment: Yup.object().shape({
          targetInformation: Yup.string().nullable(),
        }),
        moderatedTaskSettings:
          session.studyType === StudyType.MODERATED
            ? getModeratedTaskSettingValidationSchema(session.format)
            : Yup.object().optional(),
      }),
    [participantLimits, session],
  );

  const initialValues = useMemo(() => {
    if (!taskSetting || !recruitment) {
      return undefined;
    }
    const moderatedTaskSettings =
      session.studyType === StudyType.MODERATED && 'format' in taskSetting
        ? getModeratedTaskSettingInitialValue(taskSetting)
        : undefined;

    return {
      session: getSessionInitialValue(session),
      recruitment: {
        countryKeys: recruitment?.countryKeys || [],
        targetInformation: recruitment?.targetInformation || '',
      },
      moderatedTaskSettings,
    };
  }, [taskSetting, recruitment, session]);

  const redirectIfDraft = useRedirectIfDraft(session);

  const closeConfirmationModal = useCallback(() => {
    baseCloseConfirmationModal();
    redirectIfDraft();
  }, [baseCloseConfirmationModal, redirectIfDraft]);

  const submit = useCallback(
    async (values: typeof initialValues, helpers: FormikHelpers<any>) => {
      if (!values) {
        return;
      }

      let quotasMustBeUpdated = false;
      if (
        values.session.numberOfParticipantRequired !==
        session.numberOfParticipantRequired
      ) {
        quotasMustBeUpdated = !!recruitment?.criteria?.some(
          (criterion) => criterion.quotas !== null,
        );

        if (quotasMustBeUpdated) {
          const criteria = recruitment?.criteria;
          if (criteria) {
            updateCriteria(
              criteria?.map((criterion) => {
                if (!criterion.quotas) return criterion;
                return {
                  ...criterion,
                  quotas: updateQuotasValues(
                    criterion?.quotas,
                    session.numberOfParticipantRequired,
                    values.session.numberOfParticipantRequired,
                  ),
                };
              }),
            );
            openConfirmationModal();
          }
        }
      }

      try {
        await Promise.all([
          updateStudy(values.session),
          updateRecruitment(values.recruitment),
          values.moderatedTaskSettings
            ? updateModeratedTaskSetting(values.moderatedTaskSettings)
            : undefined,
        ]);

        await markStepAsValidated({ step: 'settings' });
        toast.saved();
        !quotasMustBeUpdated && redirectIfDraft();
      } catch (e) {
        helpers.setStatus(e);
        toast.exception(e);
      }
    },
    [
      recruitment,
      updateStudy,
      updateRecruitment,
      updateModeratedTaskSetting,
      markStepAsValidated,
      toast,
      redirectIfDraft,
      session,
      openConfirmationModal,
      updateCriteria,
    ],
  );

  if (!initialValues) {
    return <CenteredLoader />;
  }

  return (
    <>
      <CenteredModal
        isOpen={confirmationModalOpened}
        onRequestClose={closeConfirmationModal}
        title={
          <Trans i18nKey="editSession.targeting.quotas.autoUpdatedTitle" />
        }
        text={<Trans i18nKey="editSession.targeting.quotas.autoUpdatedText" />}
        buttons={[
          <Button
            text={
              <Trans i18nKey="editSession.targeting.quotas.autoUpdatedButton" />
            }
            onClick={closeConfirmationModal}
            key="confirm"
            type="button"
          />,
        ]}
      />
      <Formik
        initialValues={initialValues}
        onSubmit={submit}
        validationSchema={validationSchema}
      >
        <Form>
          <EditSessionLayout
            session={session}
            footer={
              <FormikConsumer>
                {({ values }) => (
                  <SessionSubmitLine
                    session={session}
                    numberOfParticipantRequired={
                      values.session?.numberOfParticipantRequired
                    }
                    countryKeys={values.recruitment?.countryKeys}
                  />
                )}
              </FormikConsumer>
            }
          >
            <StudySettingFields
              session={session}
              name="session"
              recruitmentName="recruitment"
              participantLimits={participantLimits}
              priceTier={priceTier}
            />
            {session.studyType === StudyType.MODERATED && (
              <>
                <ModeratedTaskSettingFields
                  session={session}
                  name="moderatedTaskSettings"
                />
                <NDADropzone session={session} />
              </>
            )}
          </EditSessionLayout>
        </Form>
      </Formik>
    </>
  );
};
